// src/components/Reports/ReportsView.js
import React, { useEffect, useState } from "react";
import {
  FaFilePdf,
  FaFilePowerpoint,
  FaFolderOpen,
} from "react-icons/fa";

import LoadingScreen from "../LoadingScreen";
import ReportViewerModal from "./ReportViewerModal";
import "../../styles/ReportsView.css";

import {
  fetchReports,
  requestReportAccess,
} from "../../services/reportsApi";


function ReportCard({ report, onOpen }) {
  const hasPptx = report?.has_pptx === true;

  return (
    <button
      type="button"
      className="report-card"
      onClick={() => onOpen(report)}
    >
      <div className="report-icon">
        <FaFilePdf aria-hidden="true" />
      </div>

      <div className="report-info">
        <h3>{report.title}</h3>

        <span className="report-meta">
          {report.created_at}
        </span>

        <div className="report-formats">
          <span className="report-format report-format-pdf">
            <FaFilePdf aria-hidden="true" />
            PDF
          </span>

          {hasPptx && (
            <span className="report-format report-format-pptx">
              <FaFilePowerpoint aria-hidden="true" />
              PPTX
            </span>
          )}
        </div>
      </div>
    </button>
  );
}


export default function ReportsView() {
  const [loading, setLoading] = useState(false);
  const [reports, setReports] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [pdfSrc, setPdfSrc] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);

  // Navegación
  const [selectedType, setSelectedType] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedBrand, setSelectedBrand] = useState(null);

  useEffect(() => {
    let mounted = true;

    setLoading(true);

    fetchReports()
      .then((data) => {
        if (!mounted) return;

        const items = Array.isArray(data)
          ? data
          : data?.items || [];

        setReports(items);
      })
      .catch((error) => {
        console.error(
          "Error cargando reportes:",
          error
        );
      })
      .finally(() => {
        if (mounted) {
          setLoading(false);
        }
      });

    return () => {
      mounted = false;
    };
  }, []);

  const openReport = async (report) => {
    if (!report?.report_id) return;

    setSelected(report);
    setIsOpen(true);
    setPdfSrc("");
    setPdfLoading(true);

    try {
      // La vista previa siempre utiliza el PDF.
      const data = await requestReportAccess(
        report.report_id,
        "preview",
        "pdf"
      );

      if (!data?.url) {
        throw new Error(
          "La API no devolvió una URL de vista previa."
        );
      }

      setPdfSrc(data.url);
    } catch (error) {
      console.error(
        "Error abriendo reporte:",
        error
      );

      setPdfSrc("");
    } finally {
      setPdfLoading(false);
    }
  };

  const downloadReport = async (
    report,
    format = "pdf"
  ) => {
    if (!report?.report_id) return;

    const normalizedFormat = String(format)
      .trim()
      .toLowerCase();

    // Defensa adicional de UI.
    if (!["pdf", "pptx"].includes(normalizedFormat)) {
      console.error(
        "Formato de descarga no permitido."
      );
      return;
    }

    if (
      normalizedFormat === "pptx" &&
      report?.has_pptx !== true
    ) {
      console.error(
        "El reporte no está disponible en PPTX."
      );
      return;
    }

    try {
      const data = await requestReportAccess(
        report.report_id,
        "download",
        normalizedFormat
      );

      if (!data?.url) {
        throw new Error(
          "La API no devolvió una URL de descarga."
        );
      }

      window.open(
        data.url,
        "_blank",
        "noopener,noreferrer"
      );
    } catch (error) {
      console.error(
        `Error descargando ${normalizedFormat.toUpperCase()}:`,
        error
      );
    }
  };

  const closeModal = () => {
    setIsOpen(false);
    setSelected(null);
    setPdfSrc("");
    setPdfLoading(false);
  };

  const norm = (value) =>
    String(value || "").trim();

  const isOcesa = (brand) =>
    norm(brand).toUpperCase() === "OCESA";

  // =========================
  // Nivel 1: report_type
  // =========================
  const byType = reports.reduce((accumulator, report) => {
    const type = norm(report.report_type) || "Sin tipo";

    accumulator[type] = accumulator[type] || [];
    accumulator[type].push(report);

    return accumulator;
  }, {});

  const types = Object.keys(byType).sort();

  const typeReports = selectedType
    ? byType[selectedType] || []
    : [];

  // =========================
  // Nivel 2: event_id
  // =========================
  const byEvent = typeReports.reduce(
    (accumulator, report) => {
      const event = norm(report.event_id);

      if (!event) {
        accumulator.__direct =
          accumulator.__direct || [];

        accumulator.__direct.push(report);
      } else {
        accumulator[event] =
          accumulator[event] || [];

        accumulator[event].push(report);
      }

      return accumulator;
    },
    {}
  );

  const events = Object.keys(byEvent)
    .filter((key) => key !== "__direct")
    .sort();

  const eventReports =
    selectedEvent && byEvent[selectedEvent]
      ? byEvent[selectedEvent]
      : [];

  // =========================
  // Nivel 3: brand
  // =========================
  const byBrand = eventReports.reduce(
    (accumulator, report) => {
      const brand = norm(report.brand);

      if (
        !brand ||
        brand.toLowerCase() === "null" ||
        isOcesa(brand)
      ) {
        accumulator.__direct =
          accumulator.__direct || [];

        accumulator.__direct.push(report);
      } else {
        accumulator[brand] =
          accumulator[brand] || [];

        accumulator[brand].push(report);
      }

      return accumulator;
    },
    {}
  );

  const brands = Object.keys(byBrand)
    .filter((key) => key !== "__direct")
    .sort();

  const brandReports =
    selectedBrand && byBrand[selectedBrand]
      ? byBrand[selectedBrand]
      : [];

  if (loading) {
    return (
      <LoadingScreen text="Cargando reportes..." />
    );
  }

  return (
    <div className="reports-view">
      <div className="reports-header">
        <h2>Reportes</h2>

        <p>
          Previsualiza los reportes y descarga los
          formatos disponibles.
        </p>
      </div>

      {/* Nivel 1: tipo de reporte */}
      {!selectedType && (
        <div className="reports-grid">
          {types.map((type) => (
            <button
              type="button"
              key={type}
              className="report-card report-folder"
              onClick={() => {
                setSelectedType(type);
                setSelectedEvent(null);
                setSelectedBrand(null);
              }}
            >
              <div className="report-icon folder-icon">
                <FaFolderOpen aria-hidden="true" />
              </div>

              <div className="report-info">
                <h3>{type}</h3>

                <span className="report-meta">
                  {byType[type].length} reportes
                </span>
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Nivel 2: evento */}
      {selectedType && !selectedEvent && (
        <>
          <div className="reports-folder-header">
            <button
              type="button"
              className="reports-back"
              onClick={() => setSelectedType(null)}
            >
              ← Volver
            </button>

            <h3 className="reports-folder-title">
              {selectedType}
            </h3>
          </div>

          <div className="reports-grid">
            {(byEvent.__direct || []).map((report) => (
              <ReportCard
                key={report.report_id}
                report={report}
                onOpen={openReport}
              />
            ))}

            {events.map((event) => (
              <button
                type="button"
                key={event}
                className="report-card report-folder"
                onClick={() => {
                  setSelectedEvent(event);
                  setSelectedBrand(null);
                }}
              >
                <div className="report-icon folder-icon">
                  <FaFolderOpen aria-hidden="true" />
                </div>

                <div className="report-info">
                  <h3>{event}</h3>

                  <span className="report-meta">
                    {byEvent[event].length} reportes
                  </span>
                </div>
              </button>
            ))}
          </div>
        </>
      )}

      {/* Nivel 3: marca */}
      {selectedType &&
        selectedEvent &&
        !selectedBrand && (
          <>
            <div className="reports-folder-header">
              <button
                type="button"
                className="reports-back"
                onClick={() => setSelectedEvent(null)}
              >
                ← Volver
              </button>

              <h3 className="reports-folder-title">
                {selectedType} · {selectedEvent}
              </h3>
            </div>

            <div className="reports-grid">
              {(byBrand.__direct || []).map((report) => (
                <ReportCard
                  key={report.report_id}
                  report={report}
                  onOpen={openReport}
                />
              ))}

              {brands.map((brand) => (
                <button
                  type="button"
                  key={brand}
                  className="report-card report-folder"
                  onClick={() => setSelectedBrand(brand)}
                >
                  <div className="report-icon folder-icon">
                    <FaFolderOpen aria-hidden="true" />
                  </div>

                  <div className="report-info">
                    <h3>{brand}</h3>

                    <span className="report-meta">
                      {byBrand[brand].length} reportes
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </>
        )}

      {/* Nivel 4: reportes de marca */}
      {selectedType &&
        selectedEvent &&
        selectedBrand && (
          <>
            <div className="reports-folder-header">
              <button
                type="button"
                className="reports-back"
                onClick={() => setSelectedBrand(null)}
              >
                ← Volver
              </button>

              <h3 className="reports-folder-title">
                {selectedType} · {selectedEvent} ·{" "}
                {selectedBrand}
              </h3>
            </div>

            <div className="reports-grid">
              {brandReports.map((report) => (
                <ReportCard
                  key={report.report_id}
                  report={report}
                  onOpen={openReport}
                />
              ))}

              {!brandReports.length && (
                <div className="reports-empty">
                  No hay reportes disponibles.
                </div>
              )}
            </div>
          </>
        )}

      <ReportViewerModal
        isOpen={isOpen}
        onClose={closeModal}
        report={selected}
        pdfSrc={pdfSrc}
        isLoading={pdfLoading}
        onDownload={downloadReport}
      />
    </div>
  );
}
